// pages/support/HelpCenter.jsx
import { useState } from 'react';

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const categories = [
    { id: 'All', name: 'All Topics', icon: '📚' },
    { id: 'Account', name: 'Account', icon: '👤' },
    { id: 'Billing', name: 'Billing', icon: '💳' },
    { id: 'Campaigns', name: 'Campaigns', icon: '📊' },
    { id: 'Technical', name: 'Technical', icon: '⚙️' },
    { id: 'API', name: 'API', icon: '🔌' }
  ];

  const faqs = [
    {
      category: "Account",
      question: "How do I create an account?",
      answer: "Click 'Sign Up' button, enter your details, verify your email, and you're ready to start. It takes less than 2 minutes. You'll receive a welcome email with next steps to get started with your first campaign."
    },
    {
      category: "Account",
      question: "How do I reset my password?",
      answer: "Click 'Forgot Password' on the login page, enter your email, and follow the reset link sent to your inbox. The link is valid for 24 hours. If you don't receive the email, check your spam folder or contact support."
    },
    {
      category: "Account",
      question: "Can I have multiple users on one account?",
      answer: "Yes! Business and Enterprise plans support team collaboration. Add team members from Settings → Team Management. You can assign different permission levels for each user."
    },
    {
      category: "Billing",
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, MasterCard, Amex), UPI, net banking, and international bank transfers for enterprise clients. All payments are processed securely through our encrypted payment gateway."
    },
    {
      category: "Billing",
      question: "Can I get a refund?",
      answer: "Yes, we offer a 30-day money-back guarantee. If you're not satisfied with our service within the first 30 days, contact support@adsurg.com for a full refund. No questions asked."
    },
    {
      category: "Billing",
      question: "How does billing work?",
      answer: "We charge your payment method at the beginning of each billing cycle. You'll receive an invoice via email. You can view all invoices and update payment methods in Settings → Billing."
    },
    {
      category: "Billing",
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel anytime from your account settings. No cancellation fees or long-term contracts required. Your access continues until the end of your current billing period."
    },
    {
      category: "Campaigns",
      question: "How do I create my first campaign?",
      answer: "Navigate to Campaigns → Create New → Choose your objective → Set audience and budget → Create ads → Review and Launch. Our guided setup wizard walks you through each step with helpful tips."
    },
    {
      category: "Campaigns",
      question: "What's the minimum ad spend?",
      answer: "There's no minimum ad spend requirement. You can start with as little as ₹500/day and scale as you see results. We recommend starting with at least ₹2,000/day for better optimization."
    },
    {
      category: "Campaigns",
      question: "How long until I see results?",
      answer: "Most campaigns show initial data within 24-48 hours. Significant optimization results typically appear within 7-14 days as our AI learns your audience and optimizes performance."
    },
    {
      category: "Campaigns",
      question: "Can I run campaigns on multiple platforms?",
      answer: "Yes! Adsurg supports Google Ads, Facebook, Instagram, LinkedIn, Twitter, and TikTok. You can manage all platforms from one dashboard and get unified reporting."
    },
    {
      category: "Technical",
      question: "How do I install tracking pixels?",
      answer: "Go to Settings → Tracking → Copy your pixel code → Paste it in your website's header before the closing </head> tag. Use our Pixel Helper Chrome extension to verify installation."
    },
    {
      category: "Technical",
      question: "My campaigns aren't tracking conversions. What should I do?",
      answer: "Check if your pixel is properly installed using our Pixel Helper tool. Ensure conversion events are properly configured in Settings → Conversions. Clear your browser cache and test in incognito mode."
    },
    {
      category: "Technical",
      question: "How do I integrate with Google Analytics?",
      answer: "Go to Settings → Integrations → Google Analytics → Authenticate your account. Once connected, you'll see GA data in your Adsurg dashboard for comprehensive tracking."
    },
    {
      category: "API",
      question: "Do you provide an API?",
      answer: "Yes, we offer a comprehensive RESTful API for Business and Enterprise plans. Access our API documentation at developers.adsurg.com for complete integration guides and code examples."
    },
    {
      category: "API",
      question: "Where can I find API documentation?",
      answer: "Visit developers.adsurg.com for complete API documentation, code examples in multiple languages, webhooks, and integration guides. You can also test endpoints in our API playground."
    },
    {
      category: "API",
      question: "What are the API rate limits?",
      answer: "Business plans: 1,000 requests/hour. Enterprise plans: 10,000 requests/hour. Contact sales for higher limits if needed. Rate limit headers are included in all responses."
    }
  ];

  const quickActions = [
    {
      title: "Getting Started Guide",
      description: "Complete setup walkthrough",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Campaign Best Practices",
      description: "Tips for successful ads",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: "from-green-500 to-green-600"
    },
    {
      title: "Troubleshooting",
      description: "Fix common issues",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: "from-orange-500 to-orange-600"
    },
    {
      title: "API Documentation",
      description: "Developer resources",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      color: "from-purple-500 to-purple-600"
    }
  ];

  const filteredFaqs = activeCategory === 'All'
    ? faqs
    : faqs.filter(faq => faq.category === activeCategory);

  const searchedFaqs = searchQuery
    ? filteredFaqs.filter(faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
    : filteredFaqs;

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl mb-6 shadow-2xl shadow-[#00809D]/50">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-4">
            How can we help you?
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto">
            Search our knowledge base or browse categories to find answers
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-12 max-w-3xl mx-auto">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help articles, guides, FAQs..."
              className="w-full bg-gradient-to-br from-gray-900 to-black border border-white/20 rounded-2xl px-6 py-4 sm:py-5 pl-14 text-white text-base sm:text-lg placeholder-gray-500 focus:border-[#00809D] focus:outline-none transition-colors shadow-2xl"
            />
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400 absolute left-5 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-gray-400 text-sm mt-3 ml-1">
              Found {searchedFaqs.length} result{searchedFaqs.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">Quick Links</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {quickActions.map((action, index) => (
              <button
                key={index}
                className="group bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all hover:scale-105 text-left"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${action.color} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-all`}>
                  {action.icon}
                </div>
                <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">
                  {action.title}
                </h4>
                <p className="text-gray-400 text-xs sm:text-sm">{action.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6">Browse by Category</h2>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setExpandedFaq(null);
                }}
                className={`px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-medium transition-all flex items-center gap-2 text-sm sm:text-base ${activeCategory === category.id
                  ? 'bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white shadow-lg shadow-[#00809D]/50 scale-105'
                  : 'bg-gray-900 text-gray-400 border border-white/10 hover:border-[#00809D]/50 hover:text-white hover:scale-105'
                  }`}
              >
                <span className="text-lg">{category.icon}</span>
                <span>{category.name}</span>
                {activeCategory === category.id && (
                  <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
                    {category.id === 'All' ? faqs.length : faqs.filter(f => f.category === category.id).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div className="mb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white">
              {activeCategory === 'All' ? 'All Questions' : `${activeCategory} Questions`}
            </h2>
            <span className="text-gray-400 text-sm">
              {searchedFaqs.length} {searchedFaqs.length === 1 ? 'article' : 'articles'}
            </span>
          </div>

          {searchedFaqs.length > 0 ? (
            <div className="space-y-4">
              {searchedFaqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl overflow-hidden hover:border-[#00809D]/50 transition-all"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-5 sm:p-6 text-left flex items-start sm:items-center justify-between gap-4 hover:bg-white/5 transition-all"
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center shadow-lg shadow-[#00809D]/30">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-bold text-white mb-1 pr-4">
                          {faq.question}
                        </h3>
                        <span className="inline-block px-3 py-1 bg-[#00809D]/20 text-[#00809D] rounded-full text-xs font-semibold">
                          {faq.category}
                        </span>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 sm:w-6 sm:h-6 text-[#00809D] flex-shrink-0 transition-transform ${expandedFaq === index ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {expandedFaq === index && (
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-16 sm:pl-20 animate-in fade-in duration-300">
                      <div className="bg-[#00809D]/5 border border-[#00809D]/20 rounded-xl p-4 sm:p-5">
                        <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
                          {faq.answer}
                        </p>
                        <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-4 text-xs sm:text-sm">
                          <button className="text-[#00809D] hover:text-[#00a0c4] transition-colors flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                            </svg>
                            Helpful
                          </button>
                          <button className="text-gray-400 hover:text-white transition-colors flex items-center gap-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5" />
                            </svg>
                            Not helpful
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl">
              <div className="w-20 h-20 bg-gray-800/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-400 text-lg mb-2">No results found</p>
              <p className="text-gray-500 text-sm">Try different keywords or browse all categories</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('All');
                }}
                className="mt-6 text-[#00809D] font-semibold hover:text-[#00a0c4] transition-colors"
              >
                Clear search and show all
              </button>
            </div>
          )}
        </div>

        {/* Contact Support */}
        <div className="mb-12">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Still need help?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 hover:border-[#00809D]/50 transition-all group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#00809D]/50 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-[#00809D] transition-colors">
                Email Support
              </h3>
              <p className="text-gray-400 mb-6 text-sm sm:text-base">
                Get detailed answers from our support team within 24 hours
              </p>
              <a href="/contact" className="w-full bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Send Message
              </a>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 hover:border-[#00809D]/50 transition-all group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#00809D]/50 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-[#00809D] transition-colors">
                Live Chat
              </h3>
              <p className="text-gray-400 mb-6 text-sm sm:text-base">
                Chat with our team in real-time for instant assistance
              </p>
              <button className="w-full bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Start Chat
              </button>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-3xl p-6 sm:p-8 hover:border-[#00809D]/50 transition-all group">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#00809D]/50 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 group-hover:text-[#00809D] transition-colors">
                Phone Support
              </h3>
              <p className="text-gray-400 mb-6 text-sm sm:text-base">
                Talk to our experts for personalized assistance
              </p>
              <button className="w-full bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all shadow-lg shadow-[#00809D]/50 flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Us
              </button>
            </div>
          </div>
        </div>

        {/* Popular Resources */}
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 text-center">Popular Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">
                Documentation
              </h4>
              <p className="text-gray-400 text-sm mb-4">Complete platform guides and tutorials</p>
              <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                Browse Docs
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">
                Video Tutorials
              </h4>
              <p className="text-gray-400 text-sm mb-4">Step-by-step video guides</p>
              <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                Watch Videos
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-white/10 rounded-2xl p-6 hover:border-[#00809D]/50 transition-all group sm:col-span-2 lg:col-span-1">
              <div className="w-12 h-12 bg-gradient-to-br from-[#00809D] to-[#00a0c4] rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-[#00809D]/30 group-hover:scale-110 transition-all">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h4 className="text-white font-bold mb-2 text-base sm:text-lg group-hover:text-[#00809D] transition-colors">
                Community Forum
              </h4>
              <p className="text-gray-400 text-sm mb-4">Connect with other advertisers</p>
              <button className="text-[#00809D] font-semibold text-sm flex items-center gap-1 group-hover:gap-2 transition-all">
                Join Community
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
